import React from 'react'
import { cn } from '../lib/utils'
import { formatEstimatedCost } from '../lib/modelPricing'
import { hasEstimatedCost, hasUnpricedUsage, type UsageTotals } from '../lib/agentUsage'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

// ============================================================================
// Shared rendering for agent $ figures and token breakdowns (Step 6 of
// docs/TOKEN_COST_ACCURACY_PLAN.md). Every cost figure:
//   - is labelled "List-price estimate" (tooltip here, caption on the card),
//   - carries a "*" marker when part of it was priced at a family fallback
//     rate (rate_source = 'family_fallback'),
//   - carries a "+" when some usage couldn't be priced at all (the $ is a
//     floor), and
//   - reads "N unpriced" instead of a misleading $0 when nothing was priced.
// ============================================================================

export const LIST_PRICE_LABEL = 'List-price estimate'

const plural = (n: number, word: string) => `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`

function costDetailLines(totals: UsageTotals): string[] {
  const lines = [`${LIST_PRICE_LABEL}, not your actual bill.`]
  if (hasEstimatedCost(totals)) {
    lines.push(
      `* ${plural(totals.estimatedEventCount, 'event')} priced at a model-family fallback rate (exact model not in the price list yet).`
    )
  }
  if (hasUnpricedUsage(totals)) {
    lines.push(`+ ${plural(totals.unpricedEventCount, 'event')} could not be priced and are not included.`)
  }
  return lines
}

interface UsageCostTextProps {
  totals: UsageTotals
  /** Prefix for priced figures. Defaults to "~". */
  prefix?: string
  className?: string
}

/**
 * Inline "~$1.23*+" with a tooltip explaining the markers. Renders nothing
 * when there was no usage at all.
 */
export const UsageCostText: React.FC<UsageCostTextProps> = ({ totals, prefix = '~', className }) => {
  if (totals.eventCount === 0) return null

  const label =
    totals.costUsd === null
      ? `${totals.unpricedEventCount.toLocaleString()} unpriced`
      : `${prefix}${formatEstimatedCost(totals.costUsd)}${hasEstimatedCost(totals) ? '*' : ''}${
          hasUnpricedUsage(totals) ? '+' : ''
        }`

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className={cn('cursor-help underline decoration-dotted underline-offset-2', className)}>{label}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs space-y-1 text-xs">
        {costDetailLines(totals).map((line) => (
          <p key={line}>{line}</p>
        ))}
      </TooltipContent>
    </Tooltip>
  )
}

/**
 * Card-footer caption: always states the figures are a list-price estimate,
 * and spells out fallback / unpriced usage when present.
 */
export const UsageCostFootnote: React.FC<{ totals: UsageTotals; className?: string }> = ({ totals, className }) => {
  const parts = [`${LIST_PRICE_LABEL} at public list prices.`]
  if (hasEstimatedCost(totals)) parts.push('* includes usage priced at a model-family fallback rate.')
  if (hasUnpricedUsage(totals)) {
    parts.push(`+ ${plural(totals.unpricedEventCount, 'event')} on a model with no price yet — not included.`)
  }
  return <p className={cn('text-xs text-muted-foreground', className)}>{parts.join(' ')}</p>
}

const tokenFormatter = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

/** Input / output / cache read / cache write, as a compact 2x2 (4-up on sm+) grid. */
export const TokenBreakdown: React.FC<{ totals: UsageTotals; className?: string }> = ({ totals, className }) => {
  const cells: [string, number][] = [
    ['Input', totals.inputTokens],
    ['Output', totals.outputTokens],
    ['Cache read', totals.cacheReadTokens],
    ['Cache write', totals.cacheWriteTokens],
  ]
  return (
    <dl className={cn('grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4', className)}>
      {cells.map(([label, value]) => (
        <div key={label} className="min-w-0">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="font-medium tabular-nums text-foreground">{tokenFormatter.format(value)}</dd>
        </div>
      ))}
    </dl>
  )
}
